// ======================================================================
// 📁 File: BOQHeaderForm.jsx
// Path:
// src/modules/ppe/pages/engineering/boq/BOQHeaderForm.jsx
// ======================================================================
//
// PURPOSE:
//   - BOQ Master header form
//   - UI layout identical to RoutingHeaderForm.jsx
//
// BACKEND MODEL:
//   PPEBOQMasterHeader
//
// IMPORTANT:
//   - BOQ Code & Version are system generated
//   - Status is backend controlled
// ======================================================================

import React, { useEffect, useState } from "react";
import axiosClient from "../../../../api/axiosClient";

export default function BOQHeaderForm({
  value,
  onChange,
  disabled = false,
}) {
  const [productFamilies, setProductFamilies] = useState([]);
  const [productVariants, setProductVariants] = useState([]);
  const [equipmentList, setEquipmentList] = useState([]);

  // ------------------------------------------------------------
  // LOAD PRODUCT FAMILIES
  // ------------------------------------------------------------
  useEffect(() => {
    axiosClient
      .get("/api/ppe/masters/product-families")
      .then((res) => {
        setProductFamilies(res.data?.data || []);
      })
      .catch(() => setProductFamilies([]));
  }, []);

  // ------------------------------------------------------------
  // LOAD PRODUCT VARIANTS (OPTIONAL)
  // ------------------------------------------------------------
  useEffect(() => {
    if (!value.product_family_id) {
      setProductVariants([]);
      return;
    }

    axiosClient
      .get(
        `/api/ppe/masters/product-variants?product_family_id=${value.product_family_id}`
      )
      .then((res) => {
        setProductVariants(res.data?.data || []);
      })
      .catch(() => setProductVariants([]));
  }, [value.product_family_id]);

  // ------------------------------------------------------------
  // LOAD EQUIPMENT LIST
  // ------------------------------------------------------------
  useEffect(() => {
    axiosClient
      .get("/api/ppe/masters/equipment")
      .then((res) => {
        setEquipmentList(res.data?.data || []);
      })
      .catch(() => setEquipmentList([]));
  }, []);

  // ------------------------------------------------------------
  // UPDATE FIELD (CONTROLLED)
  // ------------------------------------------------------------
  const updateField = (field, fieldValue) => {
    if (disabled) return;

    onChange({
      ...value,
      [field]: fieldValue,
    });
  };

  return (
    <div className="card mb-3">
      <div className="card-header">
        <strong>BOQ Header</strong>
      </div>

      <div className="card-body">
        <div
          style={{
            display: "grid",
            gridTemplateColumns:
              "1.5fr 2fr 2fr 1.5fr",
            gap: 16,
          }}
        >
          {/* BOQ Code (AUTO) */}
          <input
            className="form-control"
            disabled
            placeholder="BOQ Code (auto)"
          />

          {/* BOQ Name */}
          <input
            className="form-control"
            placeholder="BOQ Name"
            value={value.boq_name || ""}
            disabled={disabled}
            onChange={(e) =>
              updateField("boq_name", e.target.value)
            }
          />

          {/* PRODUCT FAMILY */}
          <select
            className="form-select"
            value={value.product_family_id || ""}
            disabled={disabled}
            onChange={(e) =>
              updateField(
                "product_family_id",
                e.target.value
                  ? Number(e.target.value)
                  : null
              )
            }
          >
            <option value="">
              Select Product Family
            </option>

            {productFamilies.map((pf) => (
              <option key={pf.id} value={pf.id}>
                {pf.name} ({pf.code})
              </option>
            ))}
          </select>

          {/* VERSION */}
          <input
            className="form-control"
            disabled
            placeholder="Version (auto)"
          />

          {/* PRODUCT VARIANT */}
          <select
            className="form-select"
            value={value.product_variant_id || ""}
            disabled={disabled}
            onChange={(e) =>
              updateField(
                "product_variant_id",
                e.target.value
                  ? Number(e.target.value)
                  : null
              )
            }
          >
            <option value="">
              All Variants (Optional)
            </option>

            {productVariants.map((pv) => (
              <option key={pv.id} value={pv.id}>
                {pv.variant_code} – {pv.name}
              </option>
            ))}
          </select>

          {/* EQUIPMENT */}
          <select
            className="form-select"
            value={value.equipment_id || ""}
            disabled={disabled}
            onChange={(e) =>
              updateField(
                "equipment_id",
                e.target.value
                  ? Number(e.target.value)
                  : null
              )
            }
          >
            <option value="">
              Select Equipment
            </option>

            {equipmentList.map((eq) => (
              <option key={eq.id} value={eq.id}>
                {eq.name}
              </option>
            ))}
          </select>

          {/* STATUS */}
          <input
            className="form-control"
            disabled
            placeholder="Status"
          />

          {/* REMARKS */}
          <textarea
            className="form-control"
            rows={2}
            placeholder="Remarks"
            value={value.remarks || ""}
            disabled={disabled}
            onChange={(e) =>
              updateField("remarks", e.target.value)
            }
            style={{
              gridColumn: "1 / span 4",
            }}
          />
        </div>
      </div>
    </div>
  );
}